import { useState, useMemo, useRef, useEffect } from 'react'
import {
  Box,
  Input,
  VStack,
  Text,
  HStack,
  Badge,
  IconButton,
} from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { Home, Building2, Search, X } from 'lucide-react'
import type { UnitWithResidence } from '@/features/units/types'

interface UnitSelectorProps {
  units: UnitWithResidence[]
  value: string
  onChange: (unitId: string) => void
  error?: string
  label?: string
  placeholder?: string
}

interface GroupedUnits {
  [residenceId: string]: {
    residenceName: string
    residenceKey: string | null
    units: UnitWithResidence[]
  }
}

export function UnitSelector({
  units,
  value,
  onChange,
  error,
  label = 'Sélectionner un lot',
  placeholder = 'Rechercher un lot...',
}: UnitSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  // Group units by residence
  const groupedUnits = useMemo(() => {
    const grouped: GroupedUnits = {}

    units.forEach((unit) => {
      const residenceId = unit.residence_id
      if (!grouped[residenceId]) {
        grouped[residenceId] = {
          residenceName: unit.residences?.name || 'Sans résidence',
          residenceKey: unit.residences?.residence_key || null,
          units: [],
        }
      }
      grouped[residenceId].units.push(unit)
    })

    // Sort units within each residence by unit_number
    Object.values(grouped).forEach((group) => {
      group.units.sort((a, b) => {
        const aNum = a.unit_number
        const bNum = b.unit_number
        return aNum.localeCompare(bNum, undefined, { numeric: true })
      })
    })

    return grouped
  }, [units])

  // Filter units based on search term
  const filteredGroupedUnits = useMemo(() => {
    if (!searchTerm) return groupedUnits

    const filtered: GroupedUnits = {}
    const lowerSearch = searchTerm.toLowerCase().trim()

    Object.entries(groupedUnits).forEach(([residenceId, group]) => {
      const matchingUnits = group.units.filter((unit) => {
        const unitNumber = String(unit.unit_number || '').toLowerCase()
        const residenceName = group.residenceName.toLowerCase()
        const residenceKey = group.residenceKey?.toLowerCase() || ''

        const matches = (
          unitNumber.includes(lowerSearch) ||
          residenceName.includes(lowerSearch) ||
          residenceKey.includes(lowerSearch)
        )

        // Debug
        if (lowerSearch === '001') {
          console.log('Searching for 001:', {
            unitNumber,
            matches,
            searchTerm: lowerSearch
          })
        }

        return matches
      })

      if (matchingUnits.length > 0) {
        filtered[residenceId] = {
          ...group,
          units: matchingUnits,
        }
      }
    })

    return filtered
  }, [groupedUnits, searchTerm])

  // Get selected unit display
  const selectedUnit = useMemo(() => {
    return units.find((u) => u.id === value)
  }, [units, value])

  const getSelectedDisplay = () => {
    if (!selectedUnit) return placeholder

    const residence = selectedUnit.residences
    const residenceDisplay = residence?.residence_key
      ? `${residence.residence_key} - ${residence.name}`
      : residence?.name || 'Sans résidence'

    return `Lot ${selectedUnit.unit_number} - ${residenceDisplay}`
  }

  const handleSelectUnit = (unitId: string) => {
    onChange(unitId)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleClose = () => {
    setIsOpen(false)
    setSearchTerm('')
  }

  const [dropdownStyle, setDropdownStyle] = useState<{
    top?: number
    bottom?: number
    left: number
    width: number
  }>({ left: 0, width: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleClose()
      }
    }

    // Use capture phase to ensure we catch the event before any stopPropagation
    document.addEventListener('mousedown', handleClickOutside, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [isOpen])

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 50)
    }
  }, [isOpen])

  const handleOpen = () => {
    // Calculate dropdown position based on input position
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top

      // If not enough space below (less than 300px) and more space above, open upward
      if (spaceBelow < 300 && spaceAbove > spaceBelow) {
        setDropdownStyle({
          bottom: window.innerHeight - rect.top + 4,
          left: rect.left,
          width: rect.width,
        })
      } else {
        setDropdownStyle({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        })
      }
    }
    setIsOpen(true)
  }

  return (
    <Field.Root invalid={!!error}>
      {label && <Field.Label>{label}</Field.Label>}
      <Box position="relative" minW="500px" ref={containerRef}>
        {/* Display field */}
        <HStack gap={0} position="relative">
          <Box
            as="button"
            onClick={handleOpen}
            flex={1}
            px={3}
            py={2}
            h="40px"
            borderWidth="1px"
            borderRadius="md"
            borderRightRadius={selectedUnit ? "0" : "md"}
            borderRightWidth={selectedUnit ? "0" : "1px"}
            borderColor={error ? 'red.500' : 'gray.300'}
            bg="white"
            textAlign="left"
            cursor="pointer"
            _hover={{ borderColor: 'brand.500' }}
            _focus={{ borderColor: 'brand.500', outline: 'none', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
          >
            <Text fontSize="sm" color={selectedUnit ? 'gray.900' : 'gray.500'}>
              {getSelectedDisplay()}
            </Text>
          </Box>
          {selectedUnit && (
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Réinitialiser"
              onClick={(e) => {
                e.stopPropagation()
                onChange('')
              }}
              h="40px"
              px={2}
              borderWidth="1px"
              borderLeftWidth="1px"
              borderRadius="md"
              borderLeftRadius="0"
              borderColor={error ? 'red.500' : 'gray.300'}
              bg="white"
              _hover={{ bg: 'gray.100' }}
            >
              <X size={16} />
            </IconButton>
          )}
        </HStack>

        {/* Dropdown */}
        {isOpen && (
          <Box
            ref={dropdownRef}
            position="fixed"
            top={dropdownStyle.top}
            bottom={dropdownStyle.bottom}
            left={dropdownStyle.left}
            width={`${dropdownStyle.width}px`}
            bg="white"
            borderWidth="1px"
            borderRadius="md"
            boxShadow="2xl"
            maxH="280px"
            overflow="hidden"
            zIndex={9999}
          >
            {/* Search input */}
            <Box p={2} borderBottomWidth="1px">
              <Box position="relative">
                <Search
                  size={16}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--chakra-colors-gray-500)',
                    pointerEvents: 'none',
                  }}
                />
                <Input
                  ref={inputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={placeholder}
                  size="sm"
                  pl="36px"
                />
              </Box>
            </Box>

            {/* Units list */}
            <VStack
              align="stretch"
              gap={0}
              overflowY="auto"
              maxH="220px"
            >
              {Object.entries(filteredGroupedUnits).length === 0 ? (
                <Box p={4} textAlign="center">
                  <Text fontSize="sm" color="gray.500">
                    Aucun lot trouvé
                  </Text>
                </Box>
              ) : (
                Object.entries(filteredGroupedUnits).map(([residenceId, group]) => (
                  <Box key={residenceId}>
                    {/* Residence header */}
                    <Box
                      px={3}
                      py={2}
                      bg="gray.50"
                      borderBottomWidth="1px"
                      position="sticky"
                      top={0}
                      zIndex={1}
                    >
                      <HStack gap={2}>
                        <Building2 size={14} color="var(--chakra-colors-gray-600)" />
                        <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                          {group.residenceKey
                            ? `${group.residenceKey} - ${group.residenceName}`
                            : group.residenceName}
                        </Text>
                        <Badge size="xs" colorPalette="gray" variant="subtle">
                          {group.units.length}
                        </Badge>
                      </HStack>
                    </Box>

                    {/* Units in this residence */}
                    {group.units.map((unit) => (
                      <Box
                        key={unit.id}
                        as="button"
                        w="full"
                        px={3}
                        py={2}
                        textAlign="left"
                        bg={unit.id === value ? 'brand.50' : 'white'}
                        borderBottomWidth="1px"
                        borderColor="gray.100"
                        cursor="pointer"
                        onClick={() => handleSelectUnit(unit.id)}
                        _hover={{
                          bg: unit.id === value ? 'brand.100' : 'gray.50',
                        }}
                      >
                        <HStack gap={2} justify="space-between">
                          <HStack gap={2}>
                            <Home size={14} color="var(--chakra-colors-gray-500)" />
                            <Text fontSize="sm" fontWeight="medium">
                              Lot {unit.unit_number}
                            </Text>
                          </HStack>
                          <HStack gap={2}>
                            {unit.unit_type && (
                              <Badge size="xs" colorPalette="gray" variant="subtle">
                                {unit.unit_type === 'RESIDENTIAL' && 'Résidentiel'}
                                {unit.unit_type === 'PARKING' && 'Parking'}
                                {unit.unit_type === 'CELLAR' && 'Cave'}
                                {unit.unit_type === 'COMMERCIAL' && 'Commercial'}
                              </Badge>
                            )}
                          </HStack>
                        </HStack>
                      </Box>
                    ))}
                  </Box>
                ))
              )}
            </VStack>
          </Box>
        )}
      </Box>
      {error && <Field.ErrorText>{error}</Field.ErrorText>}
    </Field.Root>
  )
}
